import {
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initFirebase } from '../lib/firebase';
import { marketRequestsCollection, marketRequestDoc } from '../lib/firestore';
import type { MarketRequest, DayOfWeek } from '../types';
import { reportError } from '../lib/error-reporting';

// ==================== CLOUD FUNCTION WRAPPERS ====================

/**
 * Submit a request for a new market
 * Used during onboarding or from settings when user's market isn't listed
 */
export async function submitMarketRequest(
  city: string,
  dayOfWeek: DayOfWeek,
  tenantId?: string
): Promise<{
  success: boolean;
  requestId: string;
  message: string;
}> {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const requestMarketFn = httpsCallable(functions, 'requestMarket');

  const result = await requestMarketFn({ city, dayOfWeek, tenantId });
  return result.data as {
    success: boolean;
    requestId: string;
    message: string;
  };
}

/**
 * Approve a market request (superadmin only)
 * Can either link to an existing market or create a new one
 */
export async function approveMarketRequest(
  requestId: string,
  options:
    | { marketId: string } // Link to existing market
    | { // Create new market
        city: string;
        dayOfWeek: DayOfWeek;
        address?: string;
        openingTime?: string;
        closingTime?: string;
      }
): Promise<{
  success: boolean;
  marketId: string;
  message: string;
}> {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const approveMarketRequestFn = httpsCallable(functions, 'approveMarketRequest');

  const result = await approveMarketRequestFn({ requestId, ...options });
  return result.data as {
    success: boolean;
    marketId: string;
    message: string;
  };
}

/**
 * Reject a market request (superadmin only)
 */
export async function rejectMarketRequest(
  requestId: string,
  reason?: string
): Promise<{
  success: boolean;
  message: string;
}> {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const rejectMarketRequestFn = httpsCallable(functions, 'rejectMarketRequest');

  const result = await rejectMarketRequestFn({ requestId, reason });
  return result.data as {
    success: boolean;
    message: string;
  };
}

// ==================== FIRESTORE QUERIES ====================

/**
 * Get a market request by ID
 */
export async function getMarketRequestById(
  requestId: string
): Promise<MarketRequest | null> {
  const { db } = initFirebase();
  const requestDoc = await getDoc(marketRequestDoc(db, requestId));

  if (!requestDoc.exists()) {
    return null;
  }

  return requestDoc.data();
}

/**
 * List all pending market requests (superadmin only)
 * Used in admin dashboard to review requests
 */
export async function listPendingMarketRequests(): Promise<MarketRequest[]> {
  const { db } = initFirebase();
  const requestsQuery = query(
    marketRequestsCollection(db),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(requestsQuery);
  return snapshot.docs.map((doc) => doc.data());
}

/**
 * List all market requests (superadmin only)
 * Can filter by status
 */
export async function listMarketRequests(
  status?: 'pending' | 'approved' | 'rejected'
): Promise<MarketRequest[]> {
  const { db } = initFirebase();

  let requestsQuery;
  if (status) {
    requestsQuery = query(
      marketRequestsCollection(db),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
  } else {
    requestsQuery = query(
      marketRequestsCollection(db),
      orderBy('createdAt', 'desc')
    );
  }

  const snapshot = await getDocs(requestsQuery);
  return snapshot.docs.map((doc) => doc.data());
}

/**
 * List market requests made by a specific user
 * Used to show user their own pending/past requests
 */
export async function listMarketRequestsByUser(
  userId: string
): Promise<MarketRequest[]> {
  const { db } = initFirebase();
  const requestsQuery = query(
    marketRequestsCollection(db),
    where('requestedBy', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(requestsQuery);
  return snapshot.docs.map((doc) => doc.data());
}

/**
 * List market requests associated with a tenant
 * Used to show tenant-specific pending requests
 */
export async function listMarketRequestsByTenant(
  tenantId: string
): Promise<MarketRequest[]> {
  const { db } = initFirebase();
  const requestsQuery = query(
    marketRequestsCollection(db),
    where('tenantId', '==', tenantId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(requestsQuery);
  return snapshot.docs.map((doc) => doc.data());
}

// ==================== REAL-TIME SUBSCRIPTIONS ====================

/**
 * Subscribe to pending market requests (superadmin only)
 * Used for real-time updates in admin dashboard
 */
export function onPendingMarketRequestsSnapshot(
  callback: (requests: MarketRequest[]) => void
): Unsubscribe {
  const { db } = initFirebase();
  const requestsQuery = query(
    marketRequestsCollection(db),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    requestsQuery,
    (snapshot) => {
      const requests = snapshot.docs.map((doc) => doc.data());
      callback(requests);
    },
    (error) => {
      reportError(error, { context: 'onPendingMarketRequestsSnapshot' });
    }
  );
}

/**
 * Subscribe to market requests for a specific user
 * Used to show real-time status updates on user's requests
 */
export function onUserMarketRequestsSnapshot(
  userId: string,
  callback: (requests: MarketRequest[]) => void
): Unsubscribe {
  const { db } = initFirebase();
  const requestsQuery = query(
    marketRequestsCollection(db),
    where('requestedBy', '==', userId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    requestsQuery,
    (snapshot) => {
      const requests = snapshot.docs.map((doc) => doc.data());
      callback(requests);
    },
    (error) => {
      reportError(error, { context: 'onUserMarketRequestsSnapshot', userId });
    }
  );
}

/**
 * Subscribe to all market requests with optional status filter (superadmin only)
 * Used for comprehensive admin view
 */
export function onMarketRequestsSnapshot(
  callback: (requests: MarketRequest[]) => void,
  status?: 'pending' | 'approved' | 'rejected'
): Unsubscribe {
  const { db } = initFirebase();

  let requestsQuery;
  if (status) {
    requestsQuery = query(
      marketRequestsCollection(db),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
  } else {
    requestsQuery = query(
      marketRequestsCollection(db),
      orderBy('createdAt', 'desc')
    );
  }

  return onSnapshot(
    requestsQuery,
    (snapshot) => {
      const requests = snapshot.docs.map((doc) => doc.data());
      callback(requests);
    },
    (error) => {
      reportError(error, { context: 'onMarketRequestsSnapshot', status });
    }
  );
}

// ==================== HELPERS ====================

/**
 * Get count of pending market requests (superadmin only)
 * Used for showing notification badge in admin UI
 */
export async function getPendingMarketRequestsCount(): Promise<number> {
  const { db } = initFirebase();
  const requestsQuery = query(
    marketRequestsCollection(db),
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(requestsQuery);
  return snapshot.size;
}

/**
 * Check if user has a pending request for a specific city/day
 * Used to prevent duplicate requests during onboarding
 */
export async function hasExistingPendingRequest(
  userId: string,
  city: string,
  dayOfWeek: DayOfWeek
): Promise<boolean> {
  const { db } = initFirebase();
  const requestsQuery = query(
    marketRequestsCollection(db),
    where('requestedBy', '==', userId),
    where('city', '==', city),
    where('dayOfWeek', '==', dayOfWeek),
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(requestsQuery);
  return !snapshot.empty;
}
